import { useEffect } from "react";
import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";

import { BridgeError, call, onBridgeEvent } from "@/bridge/bridge";

/**
 * Screens that need to intercept a back press, innermost last. A stack rather
 * than one slot because an overlay opens on top of a screen that already
 * guards: React runs child effects before parent ones, so a single slot would
 * always end up holding the outer screen's guard and the overlay could never
 * take the press.
 *
 * Guards run on every render so they close over current state; the effect has
 * no dependency list for that reason.
 */
const backGuards: (() => boolean)[] = [];

export function useBackGuard(guard: () => boolean): void {
  useEffect(() => {
    backGuards.push(guard);
    return () => {
      const index = backGuards.lastIndexOf(guard);
      if (index !== -1) {
        backGuards.splice(index, 1);
      }
    };
  });
}

/**
 * One back behaviour for the whole app: step back inside this WebView while
 * there is history to step back through, and only close the WebView once we
 * are on the screen the native shell opened.
 *
 * Without this, the native back button would tear down the whole WebView from
 * three steps deep in a flow, which is not what the user asked for.
 */
export function goBack(navigate: NavigateFunction, locationKey: string): void {
  if (backGuards.at(-1)?.() === true) {
    return;
  }
  // React Router uses this key for the entry the app was opened on.
  if (locationKey !== "default") {
    void navigate(-1);
    return;
  }

  call("nav.back").catch((error: unknown) => {
    if (error instanceof BridgeError) {
      return;
    }
    throw error;
  });
}

/**
 * Closes the WebView outright, regardless of how deep the in-app history is.
 *
 * Used when the screen itself is no longer valid to be sitting on top of —
 * e.g. an expired session — so stepping back one entry would just land on
 * another broken screen instead of leaving the WebView.
 */
export function closeWebview(): void {
  call("nav.back").catch((error: unknown) => {
    if (error instanceof BridgeError) {
      return;
    }
    throw error;
  });
}

/** Hands the native back press to `goBack`. */
export function useNativeBack(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(
    () =>
      onBridgeEvent("nav.backPressed", () => {
        goBack(navigate, location.key);
      }),
    [navigate, location.key],
  );
}
